import { Canvas } from '@react-three/fiber'
import { Suspense, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import * as THREE from 'three'

import { RunStateStore } from '../stores/RunStateStore'
import { ParentsLandingUiSync } from './ParentsLandingUiSync'
import { RunStateProvider } from './provider/RunStateProvider'
import { World } from './World'
import { WebGlCaptureBridge } from './WebGlCaptureBridge'
import { GameHud } from './ui/GameHud'
import { GameBanner } from './ui/GameBanner'
import { SettingsPauseBridge } from './SettingsPauseBridge'
import { VisibilityPauseBridge } from './VisibilityPauseBridge'
import { LevelMusicBridge } from './LevelMusicBridge'
import { TunnelTransitionSoundBridge } from './TunnelTransitionSoundBridge'
import { useSteleBreakpoint } from '../hooks/useBreakpoint'
import { Ktx2TexturePreloadBridge } from './Ktx2TexturePreloadBridge'
import { SceneReadyBridge } from './SceneReadyBridge'
import { useThreeDAssetPreload } from './preloadAssets'
import { useKtx2PreloadStatus } from './ktx2Textures'

type SceneProps = {
    onSceneReady?: () => void
    onReturnToMenu?: () => void
    onReturnToConfigurator?: () => void
    renderEnabled?: boolean
}

export const Scene = observer(function Scene({
    onSceneReady,
    onReturnToMenu,
    onReturnToConfigurator,
    renderEnabled = true,
}: SceneProps) {
    const runState = useMemo(() => new RunStateStore(), [])
    const isStele = useSteleBreakpoint()
    const threeDAssetPreload = useThreeDAssetPreload(true)
    const ktx2Preload = useKtx2PreloadStatus()
    const worldReady = threeDAssetPreload.ready && ktx2Preload.ready

    return (
        <RunStateProvider store={runState}>
            <ParentsLandingUiSync />
            <div className='relative flex flex-col w-full h-full overflow-hidden'>

                <GameBanner />
                <SettingsPauseBridge />
                <VisibilityPauseBridge />
                <LevelMusicBridge />
                <TunnelTransitionSoundBridge />

                <div className='relative flex-1 w-full min-h-0 overflow-hidden'>
                    <GameHud
                        onReturnToMenu={onReturnToMenu}
                        onReturnToConfigurator={onReturnToConfigurator}
                    />

                    <Canvas
                        dpr={isStele ? 0.5 : [1, 2]}
                        frameloop={renderEnabled ? 'always' : 'never'}
                        gl={{
                            alpha: false,
                            antialias: true,
                            powerPreference: 'high-performance',
                            preserveDrawingBuffer: false,
                            toneMapping: THREE.NoToneMapping,
                        }}
                        shadows={false}
                    >
                        <Ktx2TexturePreloadBridge />
                        {worldReady ? (
                            <Suspense fallback={null}>
                                <WebGlCaptureBridge />
                                <World />
                                <SceneReadyBridge onReady={onSceneReady} />
                            </Suspense>
                        ) : null}
                    </Canvas>
                </div>
            </div>
        </RunStateProvider>
    )
})
